<template>
	<BaseModal :title="t('title')" v-model="model" noPadding>
		<div class="px-5 pb-4">
			<div class="flex flex-col gap-4">
				<InventoryItem
					v-for="item in items"
					alt
					:src="icon(item.uuid)"
					:collection="item.collectionName"
					:name="item.name"
					:price="item.coins"
					:preloader="item.preloader"
				>
					<div
						class="relative after:absolute after:-inset-3"
						v-hover="{ active: 'scale-90', impact: 'soft' }"
						@click="removeItem(item.uuid)"
					>
						<TrashIcon class="text-label-tertiary h-5 w-5" />
					</div>
				</InventoryItem>
			</div>
		</div>
		<BottomActionsModal class="pt-4">
			<BaseButton
				size="lg"
				color="primary"
				class="relative w-full"
				@click="purchaseGifts()"
				:loading="loading"
			>
				<div>{{ t('purchase') }}</div>
				<CoinPrice :price="amount" />
			</BaseButton>
		</BottomActionsModal>
	</BaseModal>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import BaseModal from './BaseModal.vue'
import BaseButton from '../BaseButton.vue'
import InventoryItem from '../InventoryItem.vue'
import { icon } from '@/utils/urls'
import CoinPrice from '../CoinPrice.vue'
import TrashIcon from '@/assets/icons/trash.svg'
import { useMarket } from '@/stores/market'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import BottomActionsModal from '../BottomActionsModal.vue'
import { useToast } from '@/stores/toast'
import { useUser } from '@/stores/user'
import { useLayout } from '@/stores/layout'

const { t } = useI18n({
	messages: {
		en: {
			title: 'cart',
			total: 'total',
			purchase: 'purchase',
			purchaseSuccess: 'Purchased gifts for {0} coins!',
			view: 'View',
			wagerRequired: 'You need to play for {0} coins to buy gifts.'
		},
		ru: {
			title: 'корзина',
			total: 'итого',
			purchase: 'купить',
			purchaseSuccess: 'Куплено гифтов на {0} монет!',
			view: 'Посмотреть',
			wagerRequired: 'Тебе надо сыграть на {0} монет, чтобы покупать гифты.'
		}
	}
})

const props = defineProps<{
	items: {
		uuid: string
		collectionName: string
		name: string
		coins: number
		preloader?: string | null
	}[]
	amount: number
}>()

const emit = defineEmits<{
	(e: 'removeItem', uuid: string): void
}>()

const model = defineModel<boolean>()

watch(
	() => props.items,
	() => {
		if (props.items.length > 0) {
			return
		}

		model.value = false
	}
)

const removeItem = (uuid: string) => emit('removeItem', uuid)

const loading = ref(false)

const market = useMarket()
const toast = useToast()
const router = useRouter()
const user = useUser()
const layout = useLayout()

const purchaseGifts = async () => {
	if (loading.value) {
		return
	}
	if (user.displayedCoins < props.amount) {
		model.value = false
		layout.showDepositModal(Math.ceil(props.amount - user.displayedCoins))
		return
	}

	loading.value = true

	const result = await market.purchase(
		props.items.map((item) => ({
			uuid: item.uuid,
			coins: item.coins
		}))
	)

	loading.value = false

	if (!result.success) {
		if (result.wagerRemaining) {
			toast.add({
				type: 'error',
				text: t('wagerRequired', [result.wagerRemaining / 100])
			})
		}
		return
	}

	toast.add({
		type: 'success',
		text: t('purchaseSuccess', [props.amount / 100]),
		action: {
			text: t('view'),
			onClick: () => {
				router.push('/inventory')
			}
		}
	})

	market.clearSelection()

	model.value = false
}
</script>
